import { MAX_INGEST_INPUTS, MAX_MATERIALS, MAX_SPOT_SECONDS, SOCIALS, SPOTLIGHT_MS } from '@rabbitfuel/shared';
import type { ShortcutId } from './shortcuts.ts';

/**
 * Die Hilfe der Regie: je Block ein Abschnitt, dazu Fragen und Tastenkuerzel.
 *
 * Steht hier als Text und nicht als JSX, damit die Suche ihn durchsehen kann.
 * Wer einen Knopf in der Regie aendert, aendert den Satz hier mit -- eine
 * Hilfe, die etwas anderes beschreibt als das, was auf dem Bildschirm steht,
 * ist schlimmer als keine.
 */

export type HelpTopic =
  | 'aufbau'
  | 'freigaben'
  | 'startliste'
  | 'athleten-popup'
  | 'kamera'
  | 'hoeren'
  | 'hauptbild'
  | 'zweite-kamera'
  | 'vorschau'
  | 'botschaften'
  | 'ticker'
  | 'look'
  | 'werbung'
  | 'ergebnisse'
  | 'rtmp-eingang'
  | 'rtmp-ausgang'
  | 'chat'
  | 'fragen'
  | 'tasten';

export interface HelpSection {
  id: HelpTopic;
  title: string;
  intro: string;
  /** „Das kannst du einstellen". */
  can: string[];
  /** „Das geht nicht" -- Grenzen, die man sonst erst im Betrieb merkt. */
  cannot?: string[];
  /** Kuerzel, die zu diesem Block gehoeren. */
  keys?: ShortcutId[];
}

export interface HelpQuestion {
  q: string;
  a: string;
  /** Wo es ausfuehrlicher steht. */
  more?: HelpTopic;
}

export const HELP_SECTIONS: HelpSection[] = [
  {
    id: 'aufbau',
    title: 'Aufbau der Regie',
    intro:
      'Die Regie ist dein Schnittplatz. Links kommen Anfragen und Startliste herein, in der Mitte schneidest du das Bild und bedienst die Pulte, rechts läuft der Chat. Das „?“ an jedem Block springt direkt zu seiner Erklärung.',
    can: [
      'Freigaben (links) und Chat (rechts) mit dem Pfeil einklappen — die Mitte bekommt dann den Platz. Der Browser merkt sich das.',
      'Jedes Pult mit seiner Überschrift auf- und zuklappen, im Ticker-Pult auch einzelne Abschnitte.',
      'Am Handy schalten drei Reiter zwischen Freigaben, Programm und Chat um.',
      'Oben rechts Hausfarbe und Hell/Dunkel wählen. Das gilt nur für diesen Browser; das Videobild bleibt immer dunkel.',
      '„Zuschauersicht“ zeigt die Seite so, wie das Publikum sie sieht.',
      'Das Schild oben zeigt, ob die Verbindung zum Server steht: „verbunden“, „verbinde …“ oder „Verbindung weg“.',
    ],
    cannot: [
      'Der Bildschirm geht in der Regie nicht in den Ruhezustand — gewollt, aber den Akku im Blick behalten.',
      'Neu laden kostet deine eigene Kamera ein paar Sekunden, deshalb fragt der Browser vorher nach. Die Handys senden weiter.',
    ],
    keys: ['chat', 'requests', 'help'],
  },
  {
    id: 'freigaben',
    title: 'Freigaben',
    intro:
      'Wer von einem Handy aus senden will, landet zuerst hier. Erst nach deiner Freigabe steht die Kamera in der Vorschau — und erst wenn du sie ins Bild nimmst, sieht sie das Publikum.',
    can: [
      'Je Anfrage siehst du Name, Rolle, Startnummer, ob es eine Kamera oder ein Bildschirm ist, und wo das Handy im Verhältnis zum Event-Bereich steht.',
      '„Freigeben“ holt die Quelle in die Vorschau, „Ablehnen“ weist sie ab. Jede Anfrage steht zusätzlich im Chat.',
    ],
    cannot: [
      'Freigeben bringt noch nichts in den Stream — das passiert erst mit „Hauptbild“ oder „Klein“.',
      'Der Standort ist nur ein Hinweis, er sperrt niemanden aus. Entscheiden musst du.',
      'Eine abgelehnte Anfrage lässt sich hier nicht zurückholen. Die Person kann es aber erneut versuchen.',
    ],
    keys: ['requests'],
  },
  {
    id: 'startliste',
    title: 'Startliste',
    intro: 'Die Teilnehmer:innen mit Startnummer. Der Ticker schlägt daraus Nummern und Namen vor.',
    can: [
      'Nummer und Name eintragen und „Hinzufügen“.',
      '„Liste einfügen“ nimmt viele auf einmal: eine Zeile pro Person, Nummer und Name mit Semikolon getrennt (101;Mira Schulte).',
      'Mit × jemanden entfernen.',
      'Mit ✎ Headshot, Team, Info und soziale Netzwerke pflegen, mit dem Kärtchen-Symbol die Person im Bild vorstellen. Alle nacheinander zeigt der Durchgang im Block Einblendungen — siehe „Athleten-Pop-up“.',
      'RACE RESULT kann neue Teilnehmer:innen selbst ergänzen — Häkchen „Startliste“ im Block Ergebnisse.',
    ],
    cannot: [
      'Die Startnummer lässt sich nicht ändern: entfernen und neu anlegen.',
      'Streicht RACE RESULT eine Startnummer, sind auch Foto und Netzwerke dieser Person weg.',
    ],
  },
  {
    id: 'athleten-popup',
    title: 'Athleten-Pop-up',
    intro: `Stellt eine Person im Hauptbild vor: Headshot, Name, Startnummer, Team, ein Satz zu ihr und ihre sozialen Netzwerke — ${SPOTLIGHT_MS / 1000} Sekunden lang in einer Ecke. Gepflegt wird in der Startliste, eingeblendet mit dem Kärtchen-Symbol — einzeln oder alle nacheinander als Durchgang.`,
    can: [
      'In der Startliste mit ✎ pflegen: „Headshot wählen“ (wird verkleinert, „Entfernen“ nimmt es wieder weg), Name, Team/Verein und eine Info wie „Vorjahressiegerin · 38 Loops“. „Speichern“ übernimmt.',
      `Netzwerke: ${SOCIALS.map((s) => s.label).join(', ').replace(/, ([^,]*)$/, ' und $1')}. Je Feld Name oder Link; leere Felder fehlen im Pop-up.`,
      'Im Bild steht, was man sich merken kann: aus „instagram.com/anna.runs“ wird @anna.runs, bei der Webseite die Adresse ohne https://.',
      'Einblenden mit dem Kärtchen-Symbol — in der Startliste, in „Auf der Strecke“ und im Streckenkarten-Pult. Das Pop-up gleitet herein, steht und gleitet von selbst wieder hinaus, auch wenn die Regie zu ist.',
      'Das Symbol ist in der Hausfarbe gefüllt, solange das Pop-up steht; ein zweiter Druck nimmt es vorher weg. Ein neues Pop-up ersetzt das laufende.',
      'Ecke, Größe und Farbe für alle Pop-ups: im Ticker-Pult unter Einblendungen, Zeile „Athleten-Pop-up“.',
      'Durchgang (im Block Einblendungen unter „Athleten-Pop-up“): alle nacheinander statt einzeln anklicken. Wählen, wer drankommt — „Alle“ aus der Startliste oder „Im Rennen“ —, den Abstand von „direkt nacheinander“ bis „alle 5 min“ und ob auch Personen ohne Foto und Netzwerk dabei sind. „▶ Starten“ geht nach Startnummer vor.',
      'Während des Durchgangs steht dort, wer dran ist und wann die nächste Person kommt. „Überspringen“ holt sie sofort, „Stopp“ beendet den Durchgang samt Pop-up.',
      'In der Startliste zeigt jede Zeile Foto oder Initialen, Team und bis zu drei Netzwerke.',
    ],
    cannot: [
      `Die Dauer ist fest: ${SPOTLIGHT_MS / 1000} Sekunden.`,
      'Der Durchgang läuft einmal durch die Liste und hört dann auf. Er wartet, solange eine Werbepause, eine Botschaft oder ein Pop-up von Hand läuft.',
      '„Im Rennen“ heißt: auf der Strecke „unterwegs“ — das gibt es nur mit dem Ticker Strecke oder Backyard. Wer während des Durchgangs ausscheidet, wird übersprungen.',
      'Ohne Häkchen „auch ohne Foto/Netzwerk“ kommt nur dran, wer ein Foto oder ein Netzwerk hat — Team und Info allein reichen nicht.',
      'Ohne Startnummer gibt es kein Pop-up. Steht die Nummer nur auf der Strecke und nicht in der Startliste, zeigt es nur Nummer und Name.',
      'Endet ein Link nur auf eine Nummer (oft bei Facebook oder Strava), steht im Bild „Profil“ — dann lieber den Namen eintragen.',
      'Die Ecke ist ein Wunsch: Stehen dort schon das Logo, das kleine Fenster oder bei der Streckenkarte im Vollbild das Hauptbild, weicht das Pop-up aus. Andere Einblendungen in der Ecke deckt es für die Zeit zu.',
      'Während einer Werbepause oder Botschaft ist es nicht zu sehen.',
      'Zuschauer:innen können es nicht ausblenden.',
      'Foto und Netzwerke nur zeigen, wenn die Person einverstanden ist.',
    ],
  },
  {
    id: 'kamera',
    title: 'Deine Kamera und dein Bildschirm',
    intro:
      'Die Regie kann selbst senden — Kamera und Bildschirm gleichzeitig, als zwei eigene Quellen in der Vorschau.',
    can: [
      '„Kamera starten“, danach Kamera und Mikrofon im Menü wechseln. „Automatisch“ nimmt, was der Browser für richtig hält, etwa ein frisch eingestecktes Headset.',
      'Hintergrund der Kamera: Aus, Weichzeichnen oder Ersetzen durch den Bühnen-Hintergrund. Die Bildrate daneben zeigt, ob der Rechner mitkommt — unter 15 fps lieber ausschalten.',
      '„Bildschirm teilen“: Fenster, Tab oder ganzen Bildschirm, mit Mikrofon. „Anderes Fenster“ wechselt, was geteilt wird.',
      '„Bildschirm groß, Kamera klein“ setzt beides mit einem Griff ins Bild.',
      'Mikro, Bild und — beim Bildschirm — Bildschirmton direkt an der Aufnahme schalten. „Aus“ heißt: es kommt wirklich nichts mehr raus.',
      '„Beenden“ stoppt die Aufnahme und nimmt die Quelle aus der Vorschau.',
    ],
    cannot: [
      'Am iPhone lässt sich der Bildschirm im Browser nicht teilen, das geht erst ab iOS 27. Ausweg: Larix Screencaster an einen RTMP-Eingang.',
      'Nach dem Neuladen startet die Bildschirmfreigabe nicht von selbst — der Browser verlangt einen Klick auf „Wieder teilen“.',
      'Freistellen gibt es nur für die Kamera, nicht für den Bildschirm.',
    ],
    keys: ['mic', 'cam'],
  },
  {
    id: 'hoeren',
    title: 'Hören und Gegensprechen',
    intro:
      'Mit den Handys sprichst du wie am Telefon: ihr hört euch gegenseitig. Dein eigenes Mikro hörst du nie — das würde dich beim Sprechen stören und ohne Kopfhörer pfeifen.',
    can: [
      '„Hören an/aus“ legt fest, ob du die Handys überhaupt hörst. Unter „Ausgabe“ wählst du Kopfhörer oder Lautsprecher.',
      'Wer im Bild ist, groß oder klein, hat die Leitung offen: ein Live-Gespräch, es geht in den Stream.',
      '„Sprechen“ an einer Vorschau startet ein Vorgespräch mit einem Handy, das noch nicht im Bild ist. Dein Mikro ist im Stream so lange stumm. Kommt das Handy ins Bild, wird daraus ein Live-Gespräch.',
      'Den Werbespot hörst du über dasselbe Gerät mit.',
      'Sperrt der Browser den Ton, erscheint „Ton freigeben“ — einmal klicken.',
    ],
    cannot: [
      'Die Handys hören dich nur, wenn deine Kamera läuft — die Leitung nutzt ihr Mikro.',
      'Ist dein Mikro aus, hört dich auch kein Handy.',
      '„Ton aus“ am Ausgang gilt nicht fürs Gegensprechen: reden könnt ihr trotzdem.',
      'Ein Vorgespräch gilt nur in diesem Tab und endet beim Neuladen.',
    ],
    keys: ['monitor', 'talk', 'mic'],
  },
  {
    id: 'hauptbild',
    title: 'Hauptbild und Ausgang',
    intro:
      'Der große Monitor zeigt genau, was rausgeht — mit Einblendungen, Werbung und Hintergrund. Darüber stehen die Schalter für den ganzen Ausgang.',
    can: [
      '„Ton aus“ schaltet den ganzen Stream stumm, auch die nächste Kamera, auf die du schneidest. Der Knopf für den Moment, in dem im Hintergrund etwas gesagt wird, das nicht raus soll.',
      '„Abblenden“ zeigt im Stream das Standardbild statt der Kamera. Der Monitor sagt dazu, welcher Knopf abgeblendet hat.',
      '„Schwarz schalten“ nimmt die Quelle ganz aus dem Hauptbild.',
      '„Kleines Fenster aus“ bzw. „Splitscreen aus“ nimmt die zweite Kamera heraus.',
    ],
    cannot: [
      'Abblenden und Schwarz sind nicht dasselbe: abgeblendet bleibt die Kamera geschaltet und ist mit einem Klick zurück. Nach „Schwarz schalten“ wählst du neu.',
      '„Bild aus“ an einer Quelle gilt nur für diese Quelle, Abblenden für alles.',
    ],
    keys: ['mute', 'blank', 'take'],
  },
  {
    id: 'zweite-kamera',
    title: 'Kleines Fenster und Splitscreen',
    intro:
      'Eine zweite Kamera kommt entweder als kleines Fenster ins Hauptbild oder gleichberechtigt daneben als Splitscreen. Die Leiste dafür steht unter dem Monitor, sobald eine zweite Kamera im Bild ist.',
    can: [
      'Kleines Fenster: Ecke wählen und Größe von XS bis XL.',
      'Splitscreen: 50/50, 2/3 + 1/3 oder übereinander. „Tauschen“ wechselt die Seiten.',
      '„Füllen“ füllt jede Hälfte und schneidet dafür die Ränder ab. „Ganz zeigen“ zeigt das ganze Bild; der freie Rand bekommt eine unscharfe Kopie derselben Kamera.',
    ],
    cannot: [
      'Mehr als zwei Kameras gleichzeitig gehen nicht.',
      'Das kleine Fenster können Zuschauer:innen auf ihrem Gerät ausblenden, den Splitscreen nicht.',
    ],
    keys: ['second', 'secondOff', 'swap'],
  },
  {
    id: 'vorschau',
    title: 'Vorschau: deine Quellen',
    intro:
      'Hier liegt alles, was freigegeben ist — von hier aus schneidest du. Jede Kachel hat eine feste Nummer für die Tastenkürzel.',
    can: [
      '„Hauptbild“ nimmt die Quelle groß ins Bild, „Klein“ bzw. „Daneben“ als zweite Kamera.',
      'Ampel: Rot heißt nicht live, Gelb bereit machen, Grün im Stream. „Bereit“ stellt das Handy auf Gelb — die Person sieht es am Handy: gleich bist du dran.',
      'Mikro und Bild jeder Quelle schalten. Abgeschaltet wird direkt am Handy, es kommt also wirklich nichts mehr.',
      'Der Pegel im Bild zeigt, ob Ton ankommt.',
      '„Trennen“ beendet den Stream dieser Person.',
      'Die Nummer bleibt, solange die Quelle verbunden ist. Eine neue Quelle bekommt die kleinste freie Nummer.',
    ],
    cannot: [
      'Was jemand am Handy selbst ausgeschaltet hat (✋ „Sender: … aus“), kann nur diese Person wieder einschalten.',
      'Ein RTMP-Eingang hat keine Ampel und kein „Trennen“ — er wird im Block RTMP-Eingang entfernt.',
      'Ab Quelle 10 gibt es keine Tastenkürzel mehr, nur die Knöpfe.',
    ],
    keys: ['take', 'second', 'talk'],
  },
  {
    id: 'botschaften',
    title: 'Botschaften',
    intro:
      'Athlet:innen und Zuschauer:innen schicken Video- oder Sprachnachrichten bis 90 Sekunden. Du prüfst sie und spielst sie später ab — groß wie einen Werbespot, das Live-Bild läuft klein weiter.',
    can: [
      'Annehmen von: Alle, Nur vor Ort (Event-Bereich) oder Aus.',
      'Neue Botschaften ansehen, freigeben, als Favorit markieren (steht dann oben) oder löschen.',
      '„▶ Senden“ spielt eine freigegebene Botschaft im Stream, „Abbrechen“ stoppt sie.',
      'Zuschauer:innen dürfen erst drei schicken. Danach kannst du drei weitere erlauben oder die Person blockieren.',
      'Gelöschte Botschaften liegen eine Stunde im Papierkorb und lassen sich zurückholen.',
    ],
    cannot: [
      '„Nur vor Ort“ geht nur mit einem Event-Bereich (Event-Einstellungen).',
      'Nach einer Stunde im Papierkorb ist eine Botschaft endgültig weg.',
      'Die Vorschau „Ansehen“ läuft nur hier in der Regie, nicht im Stream.',
    ],
  },
  {
    id: 'ticker',
    title: 'Live-Ticker und Einblendungen',
    intro:
      'Der Ticker steht auf der Event-Seite; die Einblendungen legst du ins Videobild. Welche Ticker-Arten es gibt, hängt von der Sportart ab.',
    can: [
      'Ticker-Art: Spiel, Tennis, Strecke, Backyard, Frei oder Aus.',
      'Spiel, Tennis und Frei: Namen und Kürzel beider Seiten, Tore bzw. Punkte per Knopf, Korrektur von Hand.',
      'Strecke: wer unterwegs ist, mit Messpunkt, Zeit und Status — Nummern und Namen aus der Startliste.',
      'Backyard: km pro Loop, Loops und Feld mit −/+. Die Loops zählen zur vollen Stunde der Uhr von selbst hoch.',
      'Streckenkarte (Backyard): Die Punkte laufen mit der Runde ⌀ aus RACE RESULT, sonst mit dem Schnitt der letzten drei „zurück“, sonst mit der Standard-Rundenzeit. Weißt du, wo jemand ist: Nummer antippen, dann die Stelle auf der Strecke.',
      'Uhr: Starten/Anhalten, auf 0, ±1 min, auf eine Zeit stellen, geplante Dauer.',
      'Meldung: Tor, Karte, Wechsel, Notiz und mehr — mit Zeitmarke in den Ticker.',
      'Einblendungen im Stream: an/aus, Farbe, Position und Größe — Stand, Uhr, letztes Ereignis, Liste, Notiz, Wetter, beim Backyard auch Zähler und Streckenkarte.',
      'Streckenkarte als Vollbild (↖ ↗ ↙ ↘ = Ecke des Hauptbilds, die Größe gilt dann ihm) oder als Kästchen. Im Vollbild stehen links die Läufer mit Nummer, Name und Prognose.',
      'Athleten-Pop-up: Ecke, Größe und Farbe für alle Pop-ups, darunter der Durchgang — mehr unter „Athleten-Pop-up“.',
      'Wetter: anderen Ort eintragen; die Vorhersage kommt alle 30 Minuten neu.',
    ],
    cannot: [
      'Läuft RACE RESULT, kommen Loops und Feld von dort und −/+ ist gesperrt — außer RACE RESULT antwortet nicht.',
      'Beim Backyard ändert „Uhr stellen“ auch Loops und Strecke, deshalb kommt vorher eine Rückfrage.',
      'Ist das Event beendet, bleibt die Uhr stehen.',
      'Die Streckenkarte braucht eine Strecke: Strecken-Zone oder GPX-Datei in den Event-Einstellungen.',
      'Im Vollbild treten die anderen Einblendungen zurück — Loop, Feld und Uhr stehen in der Kopfzeile der Karte. Das Logo bleibt.',
      'Fällt RACE RESULT länger als 2 Minuten aus, rechnet die Karte mit deinen „zurück“-Messungen weiter.',
      'Zuschauer:innen können Einblendungen auf ihrem Gerät ausblenden.',
    ],
    keys: ['course'],
  },
  {
    id: 'look',
    title: 'Hintergrund und Bild',
    intro: 'Wie das Bild im Rahmen aussieht: was dahinter liegt und wie jede Kamera farblich wirkt.',
    can: [
      'Hintergrund: Schwarz, Titelbild, Vereinsfarbe oder eigenes Bild — mit Weichzeichnen und Abdunkeln.',
      'Kamerabild im Rahmen: „Ganz zeigen“ oder „Formatfüllend“.',
      'Bildkorrektur je Kamera: Neutral, Aufhellen, Warm, Kühl, Kräftig, Schwarzweiß — oder Helligkeit, Kontrast, Farbe und Stimmung von Hand.',
    ],
    cannot: [
      'Titelbild und eigenes Bild lädst du in den Event-Einstellungen hoch. Fehlt es, bleibt der Hintergrund schwarz.',
    ],
  },
  {
    id: 'werbung',
    title: 'Werbung',
    intro:
      'Sponsoren mit Logo, Bildern und Videos. Ins Bild kommen sie auf vier Wegen: Logo in der Ecke, Banner, L-Form und Werbepause.',
    can: [
      `Sponsor anlegen mit Logo und, wenn vorhanden, Spot. Danach je Sponsor bis zu ${MAX_MATERIALS} Bilder und Videos; bei jedem hakst du an, wofür es ist: Logo im Bild, Banner, L-Form, Werbepause.`,
      'Logo im Bild: Ecke, Größe und Fläche (Hell, Dunkel oder Ohne); mehrere Logos wechseln sich ab.',
      'Je Sponsor: Werbetext, Hinweis (z. B. Rabattcode), Link für den QR-Code und Farbe der L-Form — aus dem Logo oder selbst gewählt.',
      'Banner oben oder unten — als Laufband mit Werbetext oder als Bannerbilder (8 : 1), auf dunkler oder heller Fläche.',
      'L-Form: Ecke, Breite und Farbe (Sponsorfarbe, Dunkel, Hell), Video stumm in der Ecke oder L-Grafik. Die Vorschau im Block zeigt sie, bevor sie auf Sendung geht; unter „Läuft“ wählst du, welche Sponsoren drankommen.',
      'Läuft die L-Form, stehen oben in der Leiste „Weiter“ (sofort der nächste Sponsor) und „Halten“ (dieser bleibt stehen, ein Spot läuft in Schleife).',
      'Banner und L-Form: Dauer bis Aus, 30 s, 1, 2 oder 5 min oder „L-Form einmal“. Das Live-Bild rückt zusammen und bleibt zu hören.',
      'Werbepause: der Spot läuft groß mit Ton, das Live-Bild klein und stumm in der Ecke. Ecke und Größe stellst du unter „Live-Bild in der Werbepause“ ein; „Abbrechen“ beendet die Pause.',
      'Zeitplan: Aus, Automatisch (30 Sekunden vorher noch schieben oder auslassen) oder Mit Hinweis (wartet auf deine Freigabe) — für Werbepausen und, mit eigenem Abstand und eigener Dauer, für die L-Form.',
    ],
    cannot: [
      'Die Kennzeichnung „Werbung“ ist Pflicht und lässt sich nicht abschalten.',
      'Zuschauer:innen können Logo, Banner und L-Form nicht ausblenden.',
      'Banner gibt es nicht nach Zeitplan — nur Werbepausen und L-Form.',
      'Im Banner gibt es keine Sponsorfarbe: dort laufen mehrere Sponsoren zugleich.',
      'Den QR-Code kann niemand scannen, der den Stream auf dem eigenen Handy ansieht — deshalb steht der Hinweis bzw. die Adresse daneben.',
      'Läuft eine Werbepause oder Botschaft, treten Banner und L-Form solange zurück.',
      'Die sozialen Netzwerke von Athlet:innen kommen nicht über diesen Block, sondern mit dem Athleten-Pop-up aus der Startliste.',
      'Während des Spots ist dein Mikro im Stream stumm.',
      `Videos höchstens ${MAX_SPOT_SECONDS / 60} Minuten.`,
    ],
  },
  {
    id: 'ergebnisse',
    title: 'Ergebnisse (RACE RESULT)',
    intro: 'Woher die Zieleinläufe kommen: von Hand oder automatisch aus der Zeitnahme.',
    can: [
      'Von Hand: Zeiten trägst du im Ticker unter „Auf der Strecke“ ein.',
      'RACE RESULT: in RACE RESULT unter Simple API einen Schlüssel anlegen (Format JSON) und die ganze Adresse ins Feld „API-Key“ kopieren. Takt in Sekunden; wählen, was übernommen wird: Zieleinläufe, Zwischenzeiten, Startliste.',
      'Erst „Testabfrage“ — sie zeigt, welche Spalten erkannt wurden — dann „Abfrage starten“.',
      '„Spalten von Hand zuordnen“, wenn die Erkennung danebenliegt.',
    ],
    cannot: [
      'Antwortet RACE RESULT nicht, zählt beim Backyard die Uhr die Loops weiter, das Feld zählst du von Hand. Sobald RACE RESULT wieder antwortet, gelten dessen Zahlen.',
      'Läuft der Server mit RACERESULT=0, gibt es keine Abfrage.',
    ],
  },
  {
    id: 'rtmp-eingang',
    title: 'RTMP-Eingang',
    intro: 'Für Kameras, die kein Handy sind: Drohne, OBS, Larix oder der iPhone-Bildschirm über eine App.',
    can: [
      'Eingang anlegen (z. B. „Drohne“), Adresse und Schlüssel in der sendenden App eintragen. DJI Fly will die komplette Adresse, Larix, OBS und Prism Server und Schlüssel getrennt.',
      'Sobald das Signal steht, erscheint es in der Vorschau — groß, klein und Mikro wie bei einem Handy.',
      'Umbenennen, neuen Schlüssel erzeugen oder entfernen.',
    ],
    cannot: [
      `Höchstens ${MAX_INGEST_INPUTS} Eingänge.`,
      'RTMP ist unverschlüsselt — den Schlüssel nicht weitergeben. Ein neuer Schlüssel trennt sofort, wer mit dem alten sendet.',
    ],
  },
  {
    id: 'rtmp-ausgang',
    title: 'RTMP-Ausgang (YouTube, Twitch …)',
    intro: 'Das Hauptbild zusätzlich an eine Streaming-Plattform geben.',
    can: [
      'Plattform wählen (YouTube, Twitch oder ein eigener RTMP-Server), Streamschlüssel eintragen und starten.',
      '„Ausgang beenden“ stoppt die Weitergabe.',
    ],
    cannot: [
      'Der Ausgang läuft auf dem Server: Regie neu laden oder schließen beendet ihn nicht. Beenden nur mit dem Knopf.',
      'Bietet der Server keinen Ausgang an (RESTREAM=0), steht im Block nur ein Hinweis.',
    ],
  },
  {
    id: 'chat',
    title: 'Chat',
    intro: 'Der Hauptchat des Events — alle lesen mit. Anfragen und Freigaben stehen dort als graue Systemzeilen.',
    can: [
      'Mit allen schreiben; deine Nachrichten tragen das Kürzel „Vera“ für Veranstalter.',
      'Einklappen: die schmale Leiste zeigt dann, wie viele Nachrichten neu sind.',
    ],
    cannot: ['Nachrichten lassen sich nicht löschen.'],
    keys: ['chat'],
  },
];

export const HELP_QUESTIONS: HelpQuestion[] = [
  {
    q: 'Der Mikro-Knopf ist an, trotzdem kommt kein Ton.',
    a: 'Steht daneben ✋ „Sender: Mikro aus“, hat die Person am Handy selbst ausgeschaltet — nur sie kann es wieder einschalten. Sonst den Pegel in der Vorschau ansehen und prüfen, ob „Ton aus“ am Ausgang an ist.',
    more: 'vorschau',
  },
  {
    q: 'Das Handy hört mich nicht.',
    a: 'Die Leitung läuft über das Mikro deiner Kamera: Kamera starten und Mikro an. Ist das Handy noch nicht im Bild, an seiner Kachel „Sprechen“ drücken.',
    more: 'hoeren',
  },
  {
    q: 'Ich höre die Handys nicht.',
    a: '„Hören an“ prüfen und unter „Ausgabe“ das richtige Gerät wählen. Erscheint „Ton freigeben“, einmal klicken. Hörbar ist nur, wer im Bild ist oder mit wem du ein Vorgespräch führst.',
    more: 'hoeren',
  },
  {
    q: 'Der Spot läuft ohne Ton.',
    a: 'Der Browser gibt Ton erst nach einem Klick frei — einmal irgendwo auf die Seite klicken. Ist „Ton aus“ am Ausgang an, läuft auch der Spot stumm.',
    more: 'werbung',
  },
  {
    q: 'Was ist der Unterschied zwischen Abblenden, „Bild aus“ und Schwarz?',
    a: 'Abblenden: der ganze Ausgang zeigt das Standardbild, die Kamera bleibt geschaltet. „Bild aus“: nur diese eine Quelle zeigt das Standardbild. Schwarz schalten: keine Quelle mehr im Hauptbild.',
    more: 'hauptbild',
  },
  {
    q: 'Nach dem Neuladen ist mein Bildschirm weg.',
    a: 'Browser verlangen für die Bildschirmfreigabe jedes Mal einen Klick. Bei der Aufnahme steht dafür „Wieder teilen“.',
    more: 'kamera',
  },
  {
    q: 'Am iPhone kann ich den Bildschirm nicht teilen.',
    a: 'Das geht im Browser erst ab iOS 27, in Safari wie in Chrome. Bis dahin den Bildschirm mit Larix Screencaster an einen RTMP-Eingang senden.',
    more: 'rtmp-eingang',
  },
  {
    q: 'An einer Kachel steht „verbindet neu“.',
    a: 'Das Handy hat kurz die Verbindung verloren, das Bild steht so lange. Ist es innerhalb von 25 Sekunden zurück, läuft die Quelle einfach weiter, sonst fällt sie weg. Dann die Person bitten, die Seite neu zu laden.',
    more: 'vorschau',
  },
  {
    q: 'Eine Werbepause ist fällig, aber nichts passiert.',
    a: 'Im Zeitplan „Mit Hinweis“ wartet die Pause auf deine Freigabe — unten erscheint dafür eine Rückfrage. Bei „Automatisch“ startet sie von selbst.',
    more: 'werbung',
  },
  {
    q: 'Die L-Form ist dunkel statt in der Sponsorfarbe.',
    a: 'Das Logo hat keine erkennbare Farbe, etwa weiß auf durchsichtigem Grund. Beim Sponsor auf das Farbfeld klicken und eine eigene wählen.',
    more: 'werbung',
  },
  {
    q: 'Das Athleten-Pop-up erscheint nicht.',
    a: 'Während einer Werbepause oder Botschaft bleibt es unsichtbar. Ohne Startnummer ist das Kärtchen-Symbol blass und tut nichts. Steht die Nummer weder in der Startliste noch auf der Strecke, kommt oben eine Fehlermeldung.',
    more: 'athleten-popup',
  },
  {
    q: 'Das Pop-up steht in einer anderen Ecke als eingestellt.',
    a: 'In der gewählten Ecke steht schon das Logo, das kleine Fenster oder — bei der Streckenkarte im Vollbild — das Hauptbild. Dann weicht das Pop-up in eine freie Ecke aus. Eine andere Ecke wählen oder das Logo verschieben.',
    more: 'athleten-popup',
  },
  {
    q: 'Der Durchgang meldet: Niemand in der Auswahl hat ein Foto oder ein Netzwerk.',
    a: 'Ohne Häkchen „auch ohne Foto/Netzwerk“ kommt nur dran, wer in der Startliste ein Foto oder ein Netzwerk hat. Häkchen setzen oder die Personen mit ✎ pflegen.',
    more: 'athleten-popup',
  },
  {
    q: 'Der Durchgang geht nicht weiter.',
    a: 'Er wartet, solange eine Werbepause, eine Botschaft oder ein Pop-up von Hand läuft — das steht dann in seiner Zeile. Bei „alle 5 min“ zeigt die Zeile, wann die nächste Person kommt.',
    more: 'athleten-popup',
  },
  {
    q: 'Im Pop-up steht „Profil“ statt eines Namens.',
    a: 'Der eingetragene Link endet auf eine Nummer, etwa bei Facebook (profile.php?id=…) oder Strava. Statt des Links den Namen eintragen, dann steht der im Bild.',
    more: 'athleten-popup',
  },
  {
    q: 'Die Tastenkürzel tun nichts.',
    a: 'Ziffern- und Buchstabenkürzel wirken nicht, solange du in einem Textfeld tippst — einmal daneben klicken. Am Mac die ctrl-Taste nehmen, nicht ⌘. Nur Strg+M, Strg+K, Strg+S und Strg+B wirken immer.',
    more: 'tasten',
  },
];

export const HELP_GROUPS: { id: 'notfall' | 'schnitt' | 'ansicht'; title: string; note: string }[] = [
  {
    id: 'notfall',
    title: 'Notfall-Schalter',
    note: 'Wirken immer, auch während du im Chat tippst.',
  },
  {
    id: 'schnitt',
    title: 'Schneiden und Sprechen',
    note: 'Wirken nicht, solange du in einem Textfeld tippst. Die Nummer steht auf jeder Kachel in der Vorschau.',
  },
  {
    id: 'ansicht',
    title: 'Ansicht',
    note: 'Wirken nicht, solange du in einem Textfeld tippst.',
  },
];

/** Titel eines Abschnitts -- fuer das „?" an den Bloecken. */
export const helpTitle = (topic: HelpTopic): string =>
  topic === 'fragen'
    ? 'Wenn etwas nicht klappt'
    : topic === 'tasten'
      ? 'Tastenkürzel'
      : (HELP_SECTIONS.find((s) => s.id === topic)?.title ?? 'Hilfe');
